// Telemetry.tsx — Measures frame timing (FPS, dropped frames, p95/max frame time)
// over a 2s window and reports it to the server via WebSocket, while updating the
// shared snapshot for the on-screen overlay. Rendered inside the Canvas so it can
// sample the render loop via useFrame.
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGame } from '../state/context';
import { latestPerf, telemetrySocket } from '../perf';

const WINDOW_MS = 2000;
const DROP_MS = 1000 / 50; // a frame slower than 50 fps counts as a hitch

export function Telemetry() {
  const { focusedSystemId, focusedPlanetId } = useGame();
  const sock = useMemo(() => telemetrySocket(), []);
  const last = useRef(0);
  const windowStart = useRef(0);
  const times = useRef<number[]>([]);

  useFrame(() => {
    const now = performance.now();
    if (last.current === 0) {
      last.current = now;
      windowStart.current = now;
      return;
    }
    times.current.push(now - last.current);
    last.current = now;

    const elapsed = now - windowStart.current;
    if (elapsed < WINDOW_MS) {
      return;
    }

    const arr = times.current;
    const n = arr.length;
    let dropped = 0;
    let maxMs = 0;
    for (const d of arr) {
      if (d > DROP_MS) dropped++;
      if (d > maxMs) maxMs = d;
    }
    const sorted = [...arr].sort((a, b) => a - b);
    const p95 = n > 0 ? sorted[Math.min(n - 1, Math.floor(n * 0.95))] : 0;
    const fps = (n / elapsed) * 1000;
    const mode = focusedPlanetId ? 'planet' : focusedSystemId ? 'system' : 'galaxy';

    latestPerf.fps = fps;
    latestPerf.dropped = dropped;
    latestPerf.p95Ms = p95;
    latestPerf.maxMs = maxMs;
    sock.send({
      fps: Math.round(fps * 10) / 10,
      dropped,
      p95Ms: Math.round(p95 * 10) / 10,
      maxMs: Math.round(maxMs * 10) / 10,
      mode,
    });

    times.current = [];
    windowStart.current = now;
  });

  return null;
}
